import LocalOfferIcon from "@mui/icons-material/LocalOffer";
import {
  Box,
  Button,
  Checkbox,
  Grid,
  Tab,
  Tabs,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import { useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import Custom3dotString from "../../../../../components/Custom3dotString";
import CustomButton from "../../../../../components/CustomButton";
import CustomModal from "../../../../../components/CustomModal/CustomModal";
import CustomTextField from "../../../../../components/CustomTextField";
import SearchableSelect from "../../../../../components/Select/SearchableSelect";
import CustomSelect from "../../../../../components/Select/Select";
import {
  getPetsCompliantSummaryWoLoader,
  updateProcedureByIdWoLoader,
} from "../../../../../redux/reducers/petSlice";
import { showSnackBar } from "../../../../../redux/reducers/snackSlice";
import { AppColors } from "../../../../../util/AppColors";
import { routeList } from "../../../../../util/dropList";
import { billingTabEnum } from "../../../../../util/enum";
import {
  getDoneAtFromNextDueDate,
  getRightPackageUom,
  isBatchAllowedForFacility,
} from "../../../../../util/function";

const CONSUM_CATEGORIES = [
  "general consumables",
  "medical supplies",
  "surgical supplies",
  "lab consumables",
  "lab supplies",
  "cleaning supplies",
  "grooming & hygiene care",
  "fluid therapy",
  "mortuary",
];

const ADMIN_CATEGORIES = [
  "deworming",
  "diet",
  "flea & tick treatment",
  "fluid therapy",
  "grooming & hygiene care",
  "medication",
  "nutrition and supplements",
  "other parasite treatment",
  "vaccines",
];

const initConsumValues = {
  plName: null,
  stockId: null,
  batchNumber: null,
  batchId: null,
  billableQty: 1,
  uom: null,
  billedSeparately: false,
  nameErr: false,
};

const initAdminValues = {
  plName: null,
  stockId: null,
  batchNumber: null,
  batchId: null,
  quantity: 1,
  uom: null,
  dosage: "",
  route: null,
  billedSeparately: false,
  nameErr: false,
};

const AddItemsDrawer = ({ open, onClose, procedure }) => {
  const dispatch = useDispatch();
  const [activeTab, setActiveTab] = useState("consumables");
  const [consumValues, setConsumValues] = useState(initConsumValues);
  const [adminValues, setAdminValues] = useState(initAdminValues);
  const [consumSelectedItems, setConsumSelectedItems] = useState([]);
  const [adminSelectedItems, setAdminSelectedItems] = useState([]);
  const [isSaving, setIsSaving] = useState(false);

  const reqDet = useSelector((state) => state?.pet?.complaintSummary?.data);
  const appDet = reqDet?.appointment;
  const mandateDataRaw = sessionStorage.getItem("mandateData");

  const { clinicStocks, clinicDet, services, locations, facilities } =
    useMemo(() => {
      if (!mandateDataRaw) {
        return {
          clinicStocks: [],
          clinicDet: {},
          services: [],
          locations: [],
          facilities: [],
        };
      }
      try {
        const reqData = JSON.parse(mandateDataRaw);
        return {
          clinicStocks: Array.isArray(reqData?.stocks) ? reqData.stocks : [],
          clinicDet: reqData?.clinic ?? {},
          services: Array.isArray(reqData?.services) ? reqData.services : [],
          locations: Array.isArray(reqData?.locations) ? reqData.locations : [],
          facilities: Array.isArray(reqData?.facilities)
            ? reqData.facilities
            : [],
        };
      } catch {
        return {
          clinicStocks: [],
          clinicDet: {},
          services: [],
          locations: [],
          facilities: [],
        };
      }
    }, [mandateDataRaw]);

  const liveProcedure = useMemo(
    () =>
      reqDet?.procedures?.find(
        (pr) => pr?.procedureId === procedure?.procedureId,
      ) ?? procedure,
    [reqDet?.procedures, procedure],
  );

  const reqServ = useMemo(
    () =>
      services?.find((ser) => ser?.serviceId === liveProcedure?.serviceId) ??
      null,
    [services, liveProcedure?.serviceId],
  );

  const reqVar = useMemo(() => {
    const variants = reqServ?.serviceVariants ?? [];
    return (
      variants.find(
        (vnt) =>
          String(vnt?.variantId) === String(liveProcedure?.procedureVariantId),
      ) ?? variants[0]
    );
  }, [reqServ, liveProcedure?.procedureVariantId]);

  useEffect(() => {
    if (!open) return;
    setActiveTab("consumables");
    setConsumValues(initConsumValues);
    setAdminValues(initAdminValues);
    setConsumSelectedItems([]);
    setAdminSelectedItems([]);
    setIsSaving(false);
  }, [open, procedure?.procedureId]);

  const getReqFacility = () =>
    facilities?.find((fac) => fac?.facilityId === appDet?.facilityId) ?? null;

  const getBatches = (stkId = null) => {
    const reqStock = clinicStocks?.find((stc) => stc?.stockId === stkId);
    const clinicBatches = reqStock?.stockBatch?.filter(
      (btch) => btch?.createdBy === clinicDet?.id,
    );
    const reqList = clinicBatches
      ?.filter((bat) =>
        isBatchAllowedForFacility(bat, getReqFacility(), locations),
      )
      ?.filter((li) => li?.quantity > 0)
      ?.sort((a, b) => a?.quantity - b?.quantity);
    return reqList?.length > 0
      ? reqList?.map((bat) => {
          const reqLocation = locations?.find(
            (loc) => loc?.id === bat?.locationId,
          );
          return {
            name: bat?.batchNo,
            value: bat?.batchId,
            subunits: bat?.subunits,
            quantity: bat?.quantity,
            locationId: bat?.locationId,
            locationName: reqLocation?.locationName ?? "",
            uom: bat?.qtyMapName ?? "",
            packageUom: bat?.packageUom ?? "",
          };
        })
      : [
          {
            name: "Unknown",
            value: null,
            subunits: null,
            quantity: null,
            locationId: null,
            locationName: "",
            uom: "",
            packageUom: "",
          },
        ];
  };

  const getBatch = (stockId, batchId = null) => {
    if (!stockId || !batchId) return null;
    const reqStock = clinicStocks?.find((stc) => stc?.stockId === stockId);
    return (
      reqStock?.stockBatch
        ?.filter((btch) => btch?.createdBy === clinicDet?.id)
        ?.find((bat) => bat?.batchId === batchId) ?? null
    );
  };

  const getBatchByStockAndBatchId = getBatch;

  const buildBatchOption = (stkId, batchId = null, batchNo = null) => {
    const reqBat = batchId ? getBatch(stkId, batchId) : null;
    const reqLocation = locations?.find(
      (loc) => loc?.id === reqBat?.locationId,
    );
    return {
      name: batchNo ?? reqBat?.batchNo ?? "Unknown",
      value: batchId ?? reqBat?.batchId ?? null,
      subunits: reqBat?.subunits ?? null,
      quantity: reqBat?.quantity ?? null,
      locationId: reqBat?.locationId ?? null,
      locationName: reqLocation?.locationName ?? "",
      uom: reqBat?.qtyMapName ?? "",
      packageUom: reqBat?.packageUom ?? "",
    };
  };

  const getBatchesForSelect = (
    stkId = null,
    batchId = null,
    batchNo = null,
  ) => {
    const batchList = getBatches(stkId);
    if (!batchId && !batchNo) return batchList;

    const exists = batchList?.some(
      (bat) =>
        (batchId && String(bat?.value) === String(batchId)) ||
        (batchNo && bat?.name === batchNo),
    );
    if (exists) return batchList;

    const selectedOption = buildBatchOption(stkId, batchId, batchNo);
    if (
      batchList?.length === 1 &&
      batchList?.[0]?.name === "Unknown" &&
      !batchList?.[0]?.value
    ) {
      return [selectedOption];
    }
    return [selectedOption, ...(batchList ?? [])];
  };

  const getUOMListByStockId = (stkId = null) => {
    if (!stkId) return [];
    const stockMat = clinicStocks?.find((stc) => stc?.stockId === stkId);
    return (
      stockMat?.uomMappings
        ?.filter((ml) => ml?.allowSale === "Y" && ml?.show)
        ?.map((ml) => ({
          name: ml?.unit,
          value: ml?.unit,
          conversion: ml?.conversion,
          uomMappingId: ml?.uomMappingId ?? null,
        })) ?? []
    );
  };

  const resolveInitialBatchNumber = (
    stockId,
    batchId = null,
    batchNo = null,
  ) => {
    if (!stockId) return null;
    const batchList = getBatchesForSelect(stockId, batchId, batchNo);
    if (batchId || batchNo) {
      const match = batchList?.find(
        (bat) =>
          (batchId && String(bat?.value) === String(batchId)) ||
          (batchNo && bat?.name === batchNo),
      );
      if (match) return match;
      return buildBatchOption(stockId, batchId, batchNo);
    }
    return batchList?.[0] ?? null;
  };

  const buildInventoryList = (categories, addedStockIds) =>
    (clinicStocks ?? [])
      ?.filter(
        (dta) =>
          dta?.active === "Y" &&
          categories.includes(String(dta?.category ?? "").toLowerCase()),
      )
      ?.map((li) => {
        const batchList = getBatches(li?.stockId);
        const reqBatch = batchList?.find((bat) => bat?.value) ?? null;
        const requestFromPharmacy =
          !li?.autoConsumed && !batchList?.some((bat) => bat?.value != null);
        const alreadyAdded = addedStockIds.has(li?.stockId);
        const clinicBatches = (li?.stockBatch ?? []).filter(
          (btch) => btch?.createdBy === clinicDet?.id,
        );
        const totalQty = clinicBatches.reduce(
          (acc, obj) => acc + Number(obj?.quantity ?? 0),
          0,
        );
        return {
          name: li?.tradeName ?? li?.productName,
          value: li?.tradeName ?? li?.productName,
          stockId: li?.stockId,
          type: li?.category,
          subCategory: li?.subCategory ?? "",
          productName: li?.productName,
          tradeName: li?.tradeName,
          totalQty,
          batchId: reqBatch?.value ?? null,
          batchNo: reqBatch?.name ?? null,
          requestFromPharmacy,
          alreadyAdded,
          disabled: alreadyAdded,
        };
      });

  const getAddedStockIds = (detailType, queuedItems) => {
    const ids = new Set();
    (liveProcedure?.procedureDetails ?? []).forEach((det) => {
      if (det?.isDelete) return;
      if (
        String(det?.type ?? "").toLowerCase() === detailType.toLowerCase() &&
        det?.stockId
      ) {
        ids.add(det.stockId);
      }
    });
    queuedItems.forEach((item) => {
      if (item?.stockId) ids.add(item.stockId);
    });
    return ids;
  };

  const consumAddedStockIds = useMemo(
    () => getAddedStockIds("consumables", consumSelectedItems),
    [liveProcedure?.procedureDetails, consumSelectedItems],
  );

  const adminAddedStockIds = useMemo(
    () => getAddedStockIds("administered", adminSelectedItems),
    [liveProcedure?.procedureDetails, adminSelectedItems],
  );

  const consumList = useMemo(
    () => buildInventoryList(CONSUM_CATEGORIES, consumAddedStockIds),
    [clinicStocks, clinicDet?.id, appDet?.facilityId, consumAddedStockIds],
  );

  const adminList = useMemo(
    () => buildInventoryList(ADMIN_CATEGORIES, adminAddedStockIds),
    [clinicStocks, clinicDet?.id, appDet?.facilityId, adminAddedStockIds],
  );

  const onChangeConsumValue = (name, value, addObj) => {
    setConsumValues((prev) => {
      const reqObj = { ...prev, [name]: value };
      if (name === "plName" && addObj) {
        if (addObj?.alreadyAdded) return prev;
        reqObj.nameErr = false;
        reqObj.stockId = addObj?.stockId ?? null;
        reqObj.plName = value?.name ?? value?.value ?? value ?? null;
        reqObj.requestFromPharmacy = !!addObj?.requestFromPharmacy;
        if (addObj?.requestFromPharmacy) {
          reqObj.batchNumber = null;
          reqObj.batchId = null;
        } else {
          const batchNumber = resolveInitialBatchNumber(addObj?.stockId);
          reqObj.batchNumber = batchNumber;
          reqObj.batchId = batchNumber?.value ?? null;
        }
        const uomList = getUOMListByStockId(addObj?.stockId);
        reqObj.uom = uomList?.[0] ?? null;
        reqObj.billableQty = 1;
      }
      if (name === "batchNumber") {
        reqObj.batchNumber = value;
        reqObj.batchId = value?.value ?? null;
      }
      return reqObj;
    });
  };

  const onChangeAdminValue = (name, value, addObj) => {
    setAdminValues((prev) => {
      const reqObj = { ...prev, [name]: value };
      if (name === "plName" && addObj) {
        if (addObj?.alreadyAdded) return prev;
        reqObj.nameErr = false;
        reqObj.stockId = addObj?.stockId ?? null;
        reqObj.plName = value?.name ?? value?.value ?? value ?? null;
        reqObj.requestFromPharmacy = !!addObj?.requestFromPharmacy;
        if (addObj?.requestFromPharmacy) {
          reqObj.batchNumber = null;
          reqObj.batchId = null;
        } else {
          const batchNumber = resolveInitialBatchNumber(addObj?.stockId);
          reqObj.batchNumber = batchNumber;
          reqObj.batchId = batchNumber?.value ?? null;
        }
        const uomList = getUOMListByStockId(addObj?.stockId);
        reqObj.uom = uomList?.[0] ?? null;
        reqObj.dosage = "";
        reqObj.route = null;
        reqObj.quantity = 1;
      }
      if (name === "batchNumber") {
        reqObj.batchNumber = value;
        reqObj.batchId = value?.value ?? null;
      }
      return reqObj;
    });
  };

  const onApiCall = () => {
    if (appDet?.appointmentId) {
      dispatch(getPetsCompliantSummaryWoLoader(appDet?.appointmentId));
    }
  };

  const buildConsumableDetail = (formValues) => {
    const reqStock = clinicStocks?.find(
      (stc) => stc?.stockId === formValues?.stockId,
    );
    const selectedBatchId =
      formValues?.batchNumber?.value ?? formValues?.batchId ?? null;
    const reqBatDet = getBatchByStockAndBatchId(
      formValues?.stockId,
      selectedBatchId,
    );
    const uomList = getUOMListByStockId(formValues?.stockId);
    const matchedUom = formValues?.uom ?? uomList?.[0];
    const batUomMatch = uomList?.find(
      (ul) =>
        ul?.uomMappingId === reqBatDet?.mapId ||
        ul?.value === reqBatDet?.uomMapName,
    );
    const isNoAmount = formValues?.billedSeparately
      ? false
      : reqStock?.autoConsumed || !reqStock?.billable || true;

    const clinicBatches = reqStock?.stockBatch?.filter(
      (btch) => btch?.createdBy === clinicDet?.id,
    );
    const reqBat = reqStock?.autoConsumed
      ? clinicBatches?.[0]
      : selectedBatchId
        ? (clinicBatches?.find((bat) => bat?.batchId === selectedBatchId) ??
          clinicBatches?.[0])
        : clinicBatches?.[0];
    const currLocat = locations?.find((loc) => loc?.id === reqBat?.locationId);
    const stckUnitPrice =
      reqBat?.unitSellingPrice ?? clinicBatches?.[0]?.unitSellingPrice ?? 0;
    const batchConversion = Number(batUomMatch?.conversion ?? 1);
    const selectedConversion = Number(matchedUom?.conversion ?? 1);
    const itmPrice =
      batUomMatch?.uomMappingId === matchedUom?.uomMappingId
        ? Number(stckUnitPrice)
        : (Number(stckUnitPrice) * batchConversion) / selectedConversion;
    const reqUnitPrice = isNoAmount ? 0 : itmPrice;
    const taxPerent =
      reqServ?.taxApplicability?.toLowerCase() === "yes"
        ? Number(reqBat?.cgst ?? 0) + Number(reqBat?.sgst ?? 0)
        : 0;
    const reqPercent = 100 + Number(taxPerent);
    const quantity = Number(formValues?.billableQty) || 1;
    const unitPrice = Number(reqUnitPrice) / (reqPercent / 100);
    const prdTaxValue = (taxPerent / 100) * Number(unitPrice);
    const isRequest =
      formValues?.requestFromPharmacy ||
      (!reqStock?.autoConsumed && !selectedBatchId && !reqBat?.batchId);

    return {
      detailId: null,
      stockId: formValues?.stockId ?? null,
      batchId:
        selectedBatchId ?? (reqStock?.autoConsumed ? reqBat?.batchId : null),
      type: "Consumables",
      quantity,
      status: isRequest ? "requested" : true,
      remarks: isRequest ? "Requested" : "Completed",
      estimatedCost: isNoAmount ? 0 : Number(reqUnitPrice) * Number(quantity),
      name: formValues?.plName,
      procedureName: formValues?.plName,
      isDelete: false,
      serviceId: liveProcedure?.serviceId ?? null,
      tax: isNoAmount ? 0 : Number(prdTaxValue ?? 0) * Number(quantity),
      total: isNoAmount ? 0 : Number(reqUnitPrice) * Number(quantity),
      mrpPrice: isNoAmount ? 0 : Number(reqUnitPrice ?? 0),
      procedureVariantId:
        liveProcedure?.procedureVariantId ?? reqVar?.variantId,
      patientWorkbook: true,
      mainProcedure: false,
      drugSchedule: reqServ?.drugSchedule ?? null,
      hsnSacCode: reqServ?.hsnSacCode ?? null,
      packageUom: reqBat?.salesUom ?? null,
      expiryDate: reqBat?.expiryDate ?? null,
      batchNo: formValues?.batchNumber?.name || reqBat?.batchNo || null,
      billable: reqStock?.billable,
      autoConsumed: reqStock?.autoConsumed,
      linked: "Y",
      billSeparated: !!formValues?.billedSeparately,
      billType: reqStock?.autoConsumed
        ? currLocat?.category
          ? billingTabEnum?.[currLocat?.category]
          : "treatment"
        : "treatment",
      uomId: matchedUom?.uomMappingId ?? null,
      uomName: matchedUom?.value ?? null,
      billing: isRequest,
      locationId:
        formValues?.batchNumber?.locationId ??
        reqBat?.locationId ??
        currLocat?.id ??
        null,
      locationName:
        formValues?.batchNumber?.locationName ??
        currLocat?.locationName ??
        null,
    };
  };

  const buildAdministerDetail = (formValues) => {
    const reqStock = clinicStocks?.find(
      (stc) => stc?.stockId === formValues?.stockId,
    );
    const selectedBatchId =
      formValues?.batchNumber?.value ?? formValues?.batchId ?? null;
    const reqBatDet = getBatch(formValues?.stockId, selectedBatchId);
    const uomList = getUOMListByStockId(formValues?.stockId);
    const matchedUom = formValues?.uom ?? uomList?.[0];
    const batUomMatch = uomList?.find(
      (ul) =>
        ul?.uomMappingId === reqBatDet?.mapId ||
        ul?.value === reqBatDet?.uomMapName,
    );
    const isNoAmount = formValues?.billedSeparately
      ? false
      : reqStock?.autoConsumed || !reqStock?.billable || true;

    const clinicBatches = reqStock?.stockBatch?.filter(
      (btch) => btch?.createdBy === clinicDet?.id,
    );
    const reqBat = reqStock?.autoConsumed
      ? clinicBatches?.[0]
      : selectedBatchId
        ? (clinicBatches?.find((bat) => bat?.batchId === selectedBatchId) ??
          clinicBatches?.[0])
        : clinicBatches?.[0];
    const currLocat = locations?.find((loc) => loc?.id === reqBat?.locationId);
    const stckUnitPrice =
      reqBat?.unitSellingPrice ?? clinicBatches?.[0]?.unitSellingPrice ?? 0;
    const batchConversion = Number(batUomMatch?.conversion ?? 1);
    const selectedConversion = Number(matchedUom?.conversion ?? 1);
    const itmPrice =
      batUomMatch?.uomMappingId === matchedUom?.uomMappingId
        ? Number(stckUnitPrice)
        : (Number(stckUnitPrice) * batchConversion) / selectedConversion;
    const reqUnitPrice = isNoAmount ? 0 : itmPrice;
    const taxPerent =
      reqServ?.taxApplicability?.toLowerCase() === "yes"
        ? Number(reqBat?.cgst ?? 0) + Number(reqBat?.sgst ?? 0)
        : 0;
    const reqPercent = 100 + Number(taxPerent);
    const quantity = Number(formValues?.quantity) || 1;
    const reqPrice = Number(reqUnitPrice) * Number(quantity ?? 1);
    const unitPrice = Number(reqPrice) / (reqPercent / 100);
    const prdTaxValue = (taxPerent / 100) * Number(unitPrice);
    const nextDueDate =
      reqStock?.category?.toLowerCase() === "vaccines" &&
      reqServ?.nextDueDate?.length > 0
        ? getDoneAtFromNextDueDate(reqServ?.nextDueDate)
        : null;
    const isRequest =
      formValues?.requestFromPharmacy ||
      (!reqStock?.autoConsumed && !selectedBatchId && !reqBat?.batchId);

    return {
      detailId: null,
      stockId: formValues?.stockId ?? null,
      batchId:
        selectedBatchId ?? (reqStock?.autoConsumed ? reqBat?.batchId : null),
      type: "Administered",
      quantity,
      status: isRequest ? "requested" : true,
      remarks: isRequest ? "Requested" : "Completed",
      estimatedCost: isNoAmount ? 0 : (reqPrice ?? 0),
      name: formValues?.plName,
      procedureName: formValues?.plName,
      dosage: formValues?.dosage || null,
      route: formValues?.route?.value ?? formValues?.route?.name ?? null,
      isDelete: false,
      serviceId: liveProcedure?.serviceId ?? null,
      tax: isNoAmount ? 0 : Number(prdTaxValue ?? 0),
      total: isNoAmount ? 0 : Number(reqPrice ?? 0),
      mrpPrice: isNoAmount ? 0 : Number(reqUnitPrice ?? 0),
      procedureVariantId:
        liveProcedure?.procedureVariantId ?? reqVar?.variantId,
      patientWorkbook: true,
      mainProcedure: false,
      drugSchedule: reqServ?.drugSchedule ?? null,
      hsnSacCode: reqServ?.hsnSacCode ?? null,
      packageUom: reqBat?.salesUom ?? null,
      expiryDate: reqBat?.expiryDate ?? null,
      batchNo: formValues?.batchNumber?.name || reqBat?.batchNo || null,
      billable: reqStock?.billable,
      autoConsumed: reqStock?.autoConsumed,
      linked: "Y",
      billSeparated: !!formValues?.billedSeparately,
      billType: reqStock?.autoConsumed
        ? currLocat?.category
          ? billingTabEnum?.[currLocat?.category]
          : "treatment"
        : "treatment",
      uomId: matchedUom?.uomMappingId ?? null,
      uomName: matchedUom?.value ?? null,
      nextDueDate,
      billing: isRequest,
      locationId:
        formValues?.batchNumber?.locationId ??
        reqBat?.locationId ??
        currLocat?.id ??
        null,
      locationName:
        formValues?.batchNumber?.locationName ??
        currLocat?.locationName ??
        null,
    };
  };

  const buildProcedureUpdateData = (details) => {
    let tax = 0;
    let total = reqVar?.servicePrice ?? liveProcedure?.serviceCharge ?? 0;
    if (reqServ?.taxApplicability?.toLowerCase() === "yes") {
      tax +=
        (Number(reqVar?.servicePrice ?? 0) / 100) * Number(reqServ?.tax ?? 0);
      total += tax;
    }

    return {
      serviceCharge: liveProcedure?.serviceCharge,
      serviceType: liveProcedure?.serviceType,
      appointmentId: reqDet?.appointment?.appointmentId,
      procedureDetails: details,
      tax: Number(Number(tax) * Number(liveProcedure?.quantity ?? 1)),
      total: Number(Number(total) * Number(liveProcedure?.quantity ?? 1)),
      mrpPrice: Number(reqVar?.servicePrice ?? 0),
      procedureVariantId:
        reqVar?.variantId ?? liveProcedure?.procedureVariantId,
      quantity: Number(liveProcedure?.quantity ?? 1),
      hsnSacCode: reqServ?.hsnSacCode ?? null,
      paymentId: reqDet?.paymentId ?? null,
      doseInSeries: liveProcedure?.doseInSeries ?? null,
      serviceRemainderId: liveProcedure?.serviceRemainderId ?? null,
    };
  };

  const handleAddConsum = () => {
    if (!consumValues?.stockId || !consumValues?.plName) {
      setConsumValues((prev) => ({ ...prev, nameErr: true }));
      return;
    }
    setConsumSelectedItems((prev) => [
      ...prev,
      { ...consumValues, nameErr: false },
    ]);
    setConsumValues(initConsumValues);
    dispatch(
      showSnackBar({ message: "Item added successfully", type: "success" }),
    );
  };

  const handleAddAdmin = () => {
    if (!adminValues?.stockId || !adminValues?.plName) {
      setAdminValues((prev) => ({ ...prev, nameErr: true }));
      return;
    }
    setAdminSelectedItems((prev) => [
      ...prev,
      { ...adminValues, nameErr: false },
    ]);
    setAdminValues(initAdminValues);
    dispatch(
      showSnackBar({ message: "Item added successfully", type: "success" }),
    );
  };

  const handleDone = async () => {
    const consumToSave = consumValues?.stockId
      ? [...consumSelectedItems, { ...consumValues, nameErr: false }]
      : consumSelectedItems;
    const adminToSave = adminValues?.stockId
      ? [...adminSelectedItems, { ...adminValues, nameErr: false }]
      : adminSelectedItems;

    const procedureDetails = [
      ...consumToSave.map((item) => buildConsumableDetail(item)),
      ...adminToSave.map((item) => buildAdministerDetail(item)),
    ];

    if (procedureDetails.length === 0) {
      onClose();
      return;
    }

    const reqProced =
      reqDet?.procedures?.find(
        (pl) => pl?.procedureId === liveProcedure?.procedureId,
      ) ?? liveProcedure;
    if (!reqProced?.procedureId) return;

    setIsSaving(true);
    try {
      const res = await dispatch(
        updateProcedureByIdWoLoader({
          id: reqProced.procedureId,
          data: buildProcedureUpdateData(procedureDetails),
        }),
      );
      setIsSaving(false);
      if (res?.payload) {
        onApiCall();
        onClose();
      }
    } catch {
      setIsSaving(false);
    }
  };

  const adminReqStck = adminValues?.stockId
    ? clinicStocks?.find((stc) => stc?.stockId === adminValues?.stockId)
    : null;
  const dosagePackageUom = getRightPackageUom(
    getBatch(
      adminValues?.stockId,
      adminValues?.batchNumber?.value ?? adminValues?.batchId,
    )?.packageUom,
    true,
  );
  const dosageField = (
    <CustomTextField
      fullWidth
      handleChange={(e) => onChangeAdminValue("dosage", e?.target?.value)}
      value={adminValues?.dosage}
      suffix={
        dosagePackageUom?.length > 0 ? (
          <Custom3dotString
            str={`${dosagePackageUom}`}
            length={9}
            className="fs12 font-medium"
            capitalize={false}
          />
        ) : (
          ""
        )
      }
    />
  );

  const renderBillSeparately = (values, onChange) => (
    <Box mb={2}>
      <div className="flex-row">
        <Checkbox
          checked={!!values?.billedSeparately}
          onChange={(e) => onChange("billedSeparately", e.target.checked)}
          sx={{
            color: AppColors.appPrimary,
            "&.Mui-checked": { color: AppColors.appPrimary },
          }}
        />
        <div className="ml10">
          <Typography
            sx={{
              fontSize: "14px",
              fontWeight: 600,
              color: "#212121",
              mt: -0.3,
            }}
          >
            Bill separately (extra line item)
          </Typography>
          <Typography sx={{ fontSize: "12px", color: "#757575" }}>
            Suggested automatically — override if this case needs something
            different.
          </Typography>
        </div>
      </div>
    </Box>
  );

  const renderConsumablesTab = () => (
    <>
      <Box mb={3}>
        <Typography
          sx={{
            fontSize: "11px",
            fontWeight: 700,
            color: "#616161",
            letterSpacing: "0.5px",
            mb: 1,
            textTransform: "uppercase",
          }}
        >
          ITEM
        </Typography>
        <SearchableSelect
          variant="procedure"
          list={consumList ?? []}
          value={consumValues?.plName}
          error={consumValues?.nameErr}
          helperText={consumValues?.nameErr ? "Required" : ""}
          plusBtn
          mandatoryField
          isForProcedure
          handleChange={(e, value) => {
            if (value?.alreadyAdded) return;
            onChangeConsumValue("plName", value, {
              stockId: value?.stockId ?? null,
              requestFromPharmacy: !!value?.requestFromPharmacy,
              alreadyAdded: !!value?.alreadyAdded,
            });
          }}
          maxFilteredOptions={100}
          placeholder="Search inventory by item name..."
        />
      </Box>

      <Grid container spacing={2} mb={3}>
        <Grid item xs={6}>
          <Typography
            sx={{
              fontSize: "11px",
              fontWeight: 700,
              color: "#616161",
              letterSpacing: "0.5px",
              mb: 1,
              textTransform: "uppercase",
            }}
          >
            BATCH / LOT NO.
          </Typography>
          <CustomSelect
            list={getBatchesForSelect(
              consumValues?.stockId,
              consumValues?.batchNumber?.value ?? consumValues?.batchId,
              consumValues?.batchNumber?.name,
            )}
            value={consumValues?.batchNumber}
            handleChange={(e, value) =>
              onChangeConsumValue("batchNumber", value)
            }
            name="batchNumber"
            search
            isBatchNumber
            disabled={!consumValues?.stockId}
          />
        </Grid>
        <Grid item xs={3}>
          <Typography
            sx={{
              fontSize: "11px",
              fontWeight: 700,
              color: "#616161",
              letterSpacing: "0.5px",
              mb: 1,
              textTransform: "uppercase",
            }}
          >
            BILLABLE QTY
          </Typography>
          <TextField
            type="number"
            fullWidth
            size="small"
            value={consumValues?.billableQty}
            onChange={(e) => onChangeConsumValue("billableQty", e.target.value)}
            inputProps={{ min: 1 }}
            sx={{
              backgroundColor: "#fff",
              "& .MuiOutlinedInput-root": { borderRadius: "6px" },
            }}
          />
        </Grid>
        <Grid item xs={3}>
          <Typography
            sx={{
              fontSize: "11px",
              fontWeight: 700,
              color: "#616161",
              letterSpacing: "0.5px",
              mb: 1,
              textTransform: "uppercase",
            }}
          >
            UOM
          </Typography>
          <CustomSelect
            list={getUOMListByStockId(consumValues?.stockId)}
            value={consumValues?.uom}
            search
            handleChange={(e, value) => onChangeConsumValue("uom", value)}
            disabled={!consumValues?.stockId}
          />
        </Grid>
      </Grid>

      {renderBillSeparately(consumValues, onChangeConsumValue)}
    </>
  );

  const renderAdministeredTab = () => (
    <>
      <Box mb={3}>
        <Typography
          sx={{
            fontSize: "11px",
            fontWeight: 700,
            color: "#616161",
            letterSpacing: "0.5px",
            mb: 1,
            textTransform: "uppercase",
          }}
        >
          ITEM
        </Typography>
        <SearchableSelect
          variant="procedure"
          list={adminList ?? []}
          value={adminValues?.plName}
          error={adminValues?.nameErr}
          helperText={adminValues?.nameErr ? "Required" : ""}
          plusBtn
          mandatoryField
          isForProcedure
          handleChange={(e, value) => {
            if (value?.alreadyAdded) return;
            onChangeAdminValue("plName", value, {
              stockId: value?.stockId ?? null,
              requestFromPharmacy: !!value?.requestFromPharmacy,
              alreadyAdded: !!value?.alreadyAdded,
            });
          }}
          maxFilteredOptions={100}
          placeholder="Search inventory by item name..."
        />
      </Box>

      <Grid container spacing={2} mb={3}>
        <Grid item xs={6}>
          <Typography
            sx={{
              fontSize: "11px",
              fontWeight: 700,
              color: "#616161",
              letterSpacing: "0.5px",
              mb: 1,
              textTransform: "uppercase",
            }}
          >
            BATCH / LOT NO.
          </Typography>
          <CustomSelect
            list={getBatchesForSelect(
              adminValues?.stockId,
              adminValues?.batchNumber?.value ?? adminValues?.batchId,
              adminValues?.batchNumber?.name,
            )}
            value={adminValues?.batchNumber}
            handleChange={(e, value) =>
              onChangeAdminValue("batchNumber", value)
            }
            name="batchNumber"
            search
            isBatchNumber
            disabled={!adminValues?.stockId}
          />
        </Grid>
        <Grid item xs={3}>
          <Typography
            sx={{
              fontSize: "11px",
              fontWeight: 700,
              color: "#616161",
              letterSpacing: "0.5px",
              mb: 1,
              textTransform: "uppercase",
            }}
          >
            BILLABLE QTY
          </Typography>
          <TextField
            type="number"
            fullWidth
            size="small"
            value={adminValues?.quantity}
            onChange={(e) => onChangeAdminValue("quantity", e.target.value)}
            inputProps={{ min: 1 }}
            sx={{
              backgroundColor: "#fff",
              "& .MuiOutlinedInput-root": { borderRadius: "6px" },
            }}
          />
        </Grid>
        <Grid item xs={3}>
          <Typography
            sx={{
              fontSize: "11px",
              fontWeight: 700,
              color: "#616161",
              letterSpacing: "0.5px",
              mb: 1,
              textTransform: "uppercase",
            }}
          >
            UOM
          </Typography>
          <CustomSelect
            list={getUOMListByStockId(adminValues?.stockId)}
            value={adminValues?.uom}
            search
            handleChange={(e, value) => onChangeAdminValue("uom", value)}
            disabled={!adminValues?.stockId}
          />
        </Grid>
      </Grid>

      <Grid container spacing={2} mb={3}>
        <Grid item xs={6}>
          <Typography
            sx={{
              fontSize: "11px",
              fontWeight: 700,
              color: "#616161",
              letterSpacing: "0.5px",
              mb: 1,
              textTransform: "uppercase",
            }}
          >
            DOSE
          </Typography>
          {adminValues?.stockId ? (
            <Tooltip
              title={
                <>
                  <Typography className="fs12" display="block">
                    Approximated Dosage: {adminReqStck?.dosage ?? "-"}
                  </Typography>
                  <Typography className="fs12" display="block">
                    Dosage Type: {adminReqStck?.dosageType ?? "-"}
                  </Typography>
                </>
              }
            >
              <span style={{ display: "inline-block", width: "100%" }}>
                {dosageField}
              </span>
            </Tooltip>
          ) : (
            dosageField
          )}
        </Grid>
        <Grid item xs={6}>
          <Typography
            sx={{
              fontSize: "11px",
              fontWeight: 700,
              color: "#616161",
              letterSpacing: "0.5px",
              mb: 1,
              textTransform: "uppercase",
            }}
          >
            ROUTE
          </Typography>
          <CustomSelect
            list={routeList}
            value={adminValues?.route}
            search
            handleChange={(e, value) => onChangeAdminValue("route", value)}
          />
        </Grid>
      </Grid>

      {renderBillSeparately(adminValues, onChangeAdminValue)}
    </>
  );

  return (
    <CustomModal
      open={open}
      onClose={onClose}
      header="Add items"
      modal
      modalWidth={40}
      reduceHeaderSize
      hideClose
    >
      <Box
        display="flex"
        flexDirection="column"
        sx={{ height: "calc(100vh - 75px)" }}
      >
        <Box px={3} pt={2.5} flexGrow={1} overflow="auto">
          <Box
            sx={{
              backgroundColor: "#e3f2fd",
              color: AppColors.appPrimary || "#0054A6",
              borderRadius: "6px",
              px: 2,
              py: 1,
              display: "flex",
              alignItems: "center",
              gap: 1,
              fontWeight: 600,
              fontSize: "13px",
              mb: 2,
            }}
          >
            <LocalOfferIcon sx={{ fontSize: 16 }} />
            For: {liveProcedure?.procedureName || "-"}
          </Box>

          <Tabs
            value={activeTab}
            onChange={(_, value) => setActiveTab(value)}
            sx={{
              mb: 2.5,
              minHeight: 36,
              borderBottom: "1px solid #e0e0e0",
              "& .MuiTab-root": {
                textTransform: "none",
                fontWeight: 600,
                fontSize: "13px",
                minHeight: 36,
                py: 0.5,
              },
              "& .Mui-selected": {
                color: AppColors.appPrimary || "#0054A6",
              },
              "& .MuiTabs-indicator": {
                backgroundColor: AppColors.appPrimary || "#0054A6",
              },
            }}
          >
            <Tab label="Consumables" value="consumables" />
            <Tab label="Administered" value="administered" />
          </Tabs>

          <Box pb={1}>
            {activeTab === "consumables"
              ? renderConsumablesTab()
              : renderAdministeredTab()}
          </Box>
        </Box>

        <Box
          display="flex"
          justifyContent="flex-end"
          alignItems="center"
          gap={1.5}
          px={3}
          py={2}
          borderTop="1px solid #e0e0e0"
          backgroundColor="#fafafa"
        >
          <CustomButton
            text={
              activeTab === "consumables"
                ? "Add to consumables"
                : "Add to administered"
            }
            smallBtn
            disabled={isSaving}
            onClick={
              activeTab === "consumables" ? handleAddConsum : handleAddAdmin
            }
          />
          <Button
            onClick={handleDone}
            variant="outlined"
            disabled={isSaving}
            sx={{
              color: AppColors.appPrimary || "#0054A6",
              borderColor: AppColors.appPrimary || "#0054A6",
              textTransform: "none",
              fontWeight: 600,
              px: 2.5,
              height: "32px",
            }}
          >
            Done
          </Button>
        </Box>
      </Box>
    </CustomModal>
  );
};

export default AddItemsDrawer;
